import { useState } from "react";
import { Minus, Plus, Trash2, MessageSquare, Pencil, CheckCircle2, XCircle, Undo2, Pizza, Layers, UtensilsCrossed } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { CancelSentItemDialog } from "./CancelSentItemDialog";
import type { CartItem, ComboCartItem } from "@/hooks/useCart";
import type { SentOrderItem, CancelledOrderItem } from "@/hooks/useCurrentOrderItems";

export interface PendingCancellation {
  itemId: string;
  reason?: string;
}

interface PDVItemsListProps {
  items: CartItem[];
  comboItems?: ComboCartItem[];
  sentItems?: SentOrderItem[];
  cancelledItems?: CancelledOrderItem[];
  pendingCancellations?: PendingCancellation[];
  onUpdateQuantity: (productId: string, quantity: number) => void;
  onRemoveItem: (productId: string) => void;
  onEditNotes: (item: CartItem) => void;
  onRemoveCombo?: (index: number) => void;
  onUpdateComboQuantity?: (index: number, quantity: number) => void;
  onMarkForCancellation?: (itemId: string, reason?: string) => void;
  onUndoCancellation?: (itemId: string) => void;
}

export function PDVItemsList({
  items,
  comboItems = [],
  sentItems = [],
  cancelledItems = [],
  pendingCancellations = [],
  onUpdateQuantity,
  onRemoveItem,
  onEditNotes,
  onRemoveCombo,
  onUpdateComboQuantity,
  onMarkForCancellation,
  onUndoCancellation,
}: PDVItemsListProps) {
  const [cancelDialog, setCancelDialog] = useState<{
    open: boolean;
    item: SentOrderItem | null;
  }>({
    open: false,
    item: null,
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const hasSentItems = sentItems.length > 0;
  const hasCancelledItems = cancelledItems.length > 0;
  const hasNewItems = items.length > 0;
  const hasComboItems = comboItems.length > 0;

  const handleSentItemClick = (item: SentOrderItem) => {
    // Don't open dialog if already cancelled
    const isCancelled = pendingCancellations.some(c => c.itemId === item.id);
    if (isCancelled) return;
    
    if (onMarkForCancellation) {
      setCancelDialog({ open: true, item });
    }
  };

  const handleConfirmCancel = async (itemId: string, reason?: string) => {
    if (!onMarkForCancellation) return false;
    onMarkForCancellation(itemId, reason);
    return true;
  };

  // Separate active and cancelled sent items
  const activeSentItems = sentItems.filter(
    item => !pendingCancellations.some(c => c.itemId === item.id)
  );
  const cancelledSentItems = sentItems.filter(
    item => pendingCancellations.some(c => c.itemId === item.id)
  );

  const getComboIcon = (comboType: string) => {
    switch (comboType) {
      case 'pizza':
        return <Pizza className="h-4 w-4" />;
      case 'rodizio':
        return <UtensilsCrossed className="h-4 w-4" />;
      default:
        return <Layers className="h-4 w-4" />;
    }
  };

  if (!hasSentItems && !hasNewItems && !hasCancelledItems && !hasComboItems) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center py-12">
          <p className="text-sm text-[var(--pdv-text-subtle,inherit)]">(Não há itens no pedido)</p>
        </div>
      </div>
    );
  }

  // Render a cancelled item (from database)
  const renderPersistedCancelledItem = (item: CancelledOrderItem) => (
    <div 
      key={item.id} 
      className="p-3 bg-destructive/5 opacity-60"
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded flex items-center justify-center bg-destructive/20">
          <XCircle className="h-4 w-4 text-destructive" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate line-through text-muted-foreground">
            {item.product_name}
          </p>
          <p className="text-xs line-through text-muted-foreground">
            {formatCurrency(Number(item.unit_price))} x {item.quantity}
          </p>
          {item.reason && (
            <p className="text-xs text-destructive mt-1">
              Motivo: {item.reason}
            </p>
          )}
          <span className="font-semibold text-sm line-through text-muted-foreground">
            {formatCurrency(Number(item.total_price))}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <ScrollArea className="flex-1">
      {/* Persisted cancelled items (from database) */}
      {cancelledItems.length > 0 && (
        <div className="border-b">
          <div className="px-3 py-2 bg-destructive/10">
            <div className="flex items-center gap-2 text-xs font-medium text-destructive">
              <XCircle className="h-3 w-3" />
              Itens cancelados ({cancelledItems.length})
            </div>
          </div>
          <div className="divide-y">
            {cancelledItems.map(renderPersistedCancelledItem)}
          </div>
        </div>
      )}

      {/* Pending cancelled items (still to be confirmed) */}
      {cancelledSentItems.length > 0 && (
        <div className="border-b">
          <div className="px-3 py-2 bg-orange-500/10">
            <div className="flex items-center gap-2 text-xs font-medium text-orange-600">
              <XCircle className="h-3 w-3" />
              Cancelamentos pendentes ({cancelledSentItems.length})
            </div>
          </div>
          <div className="divide-y">
            {cancelledSentItems.map((item) => {
              const cancellation = pendingCancellations.find(c => c.itemId === item.id);
              return (
                <div 
                  key={item.id} 
                  className="p-3 bg-destructive/5 opacity-60"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded bg-destructive/20 flex items-center justify-center">
                      <XCircle className="h-4 w-4 text-destructive" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate line-through text-muted-foreground">
                            {item.product_name}
                          </p>
                          <p className="text-xs text-muted-foreground line-through">
                            {formatCurrency(Number(item.unit_price))} x {item.quantity}
                          </p>
                          {cancellation?.reason && (
                            <p className="text-xs text-destructive mt-1">
                              Motivo: {cancellation.reason}
                            </p>
                          )}
                        </div>
                        <span className="font-semibold text-sm shrink-0 text-muted-foreground line-through">
                          {formatCurrency(Number(item.total_price))}
                        </span>
                      </div>
                      {onUndoCancellation && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="mt-2 h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            onUndoCancellation(item.id);
                          }}
                        >
                          <Undo2 className="h-3 w-3 mr-1" />
                          Desfazer
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

        {/* Active Sent Items Section */}
        {activeSentItems.length > 0 && (
          <div className="border-b">
            <div className="px-3 py-2 bg-muted/50">
              <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                <CheckCircle2 className="h-3 w-3 text-green-500" />
                Itens já enviados ({activeSentItems.length})
              </div>
            </div>
            <div className="divide-y">
              {activeSentItems.map((item) => (
                <div 
                  key={item.id} 
                  className={`p-3 bg-muted/20 transition-colors ${
                    onMarkForCancellation 
                      ? "cursor-pointer hover:bg-destructive/10 group" 
                      : ""
                  }`}
                  onClick={() => handleSentItemClick(item)}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded bg-green-100 dark:bg-green-900/30 flex items-center justify-center group-hover:bg-destructive/20 transition-colors">
                      <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 group-hover:hidden" />
                      <XCircle className="h-4 w-4 text-destructive hidden group-hover:block" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate group-hover:text-destructive transition-colors">
                            {item.product_name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatCurrency(Number(item.unit_price))} x {item.quantity}
                          </p>
                          {item.notes && (
                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" />
                              {item.notes}
                            </p>
                          )}
                          {/* Selected addons (upsell) */}
                          {item.selected_addons && Array.isArray(item.selected_addons) && item.selected_addons.length > 0 && (
                            <div className="text-xs text-muted-foreground mt-1 ml-1 space-y-0.5">
                              {item.selected_addons.map((addon: any, i: number) => (
                                <p key={i} className="flex items-center gap-1">
                                  <span className="text-primary/60">+</span>
                                  {addon.name}
                                  <span className="text-muted-foreground/60">
                                    (+{formatCurrency(Number(addon.price))})
                                  </span>
                                </p>
                              ))}
                            </div>
                          )}
                          {/* Combo selections (flavors, choices, slots) */}
                          {item.combo_id && item.combo_selections && (
                            <div className="text-xs text-muted-foreground mt-1 ml-1 space-y-0.5">
                              {/* Pizza flavors */}
                              {item.combo_selections.selectedFlavors?.map((f, i) => (
                                <p key={i} className="flex items-center gap-1">
                                  <span className="text-muted-foreground/60">•</span>
                                  {f.productName}
                                </p>
                              ))}
                              {/* Legacy choices */}
                              {item.combo_selections.selectedChoices?.map((c, i) => (
                                <p key={i} className="flex items-center gap-1">
                                  <span className="text-muted-foreground/60">•</span>
                                  {c.choiceLabel}: {c.productName}
                                </p>
                              ))}
                              {/* Slot selections - suporte a array (legado) e objeto (tablet) */}
                              {item.combo_selections.slotSelections && (
                                Array.isArray(item.combo_selections.slotSelections) 
                                  ? (
                                    item.combo_selections.slotSelections.map((slot: any, i: number) => (
                                      slot.selectedProducts?.map((p: any, j: number) => (
                                        <p key={`${i}-${j}`} className="flex items-center gap-1">
                                          <span className="text-muted-foreground/60">•</span>
                                          {slot.slotLabel ? `${slot.slotLabel}: ` : ''}{p.productName}
                                        </p>
                                      ))
                                    ))
                                  ) : (
                                    Object.entries(item.combo_selections.slotSelections).flatMap(([slotId, products]: [string, any], i: number) => 
                                      (products as Array<{productId: string; productName: string; price: number}>)?.map((p, j) => (
                                        <p key={`${slotId}-${j}`} className="flex items-center gap-1">
                                          <span className="text-muted-foreground/60">•</span>
                                          {p.productName}
                                        </p>
                                      ))
                                    )
                                  )
                              )}
                            </div>
                          )}
                        </div>
                        <span className="font-semibold text-sm shrink-0 text-muted-foreground group-hover:text-destructive transition-colors">
                          {formatCurrency(Number(item.total_price))}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* New Items Section */}
        {hasNewItems && (
          <div>
            {(activeSentItems.length > 0 || cancelledSentItems.length > 0) && (
              <div className="px-3 py-2 bg-primary/10">
                <div className="flex items-center gap-2 text-xs font-medium text-primary">
                  <Plus className="h-3 w-3" />
                  Novos itens ({items.length})
                </div>
              </div>
            )}
            <div className="divide-y">
              {items.map((item, index) => (
                <div 
                  key={item.product.id} 
                  className="p-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    {/* Item Number */}
                    <div className="w-8 h-8 rounded bg-muted flex items-center justify-center text-sm font-medium text-muted-foreground">
                      {index + 1}
                    </div>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            {item.product.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatCurrency(Number(item.product.price))} x {item.quantity}
                          </p>
                          {item.notes && (
                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" />
                              {item.notes}
                            </p>
                          )}
                        </div>
                        <span className="font-semibold text-sm shrink-0">
                          {formatCurrency(Number(item.product.price) * item.quantity)}
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 mt-2">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => onUpdateQuantity(item.product.id, item.quantity - 1)}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => onUpdateQuantity(item.product.id, parseInt(e.target.value) || 0)}
                            className="h-7 w-12 text-center px-1 text-sm"
                            min={1}
                          />
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => onUpdateQuantity(item.product.id, item.quantity + 1)}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => onEditNotes(item)}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => onRemoveItem(item.product.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Combo Items Section */}
        {hasComboItems && (
          <div>
            {(activeSentItems.length > 0 || cancelledSentItems.length > 0 || hasNewItems) && (
              <div className="px-3 py-2 bg-primary/10">
                <div className="flex items-center gap-2 text-xs font-medium text-primary">
                  <Layers className="h-3 w-3" />
                  Combos ({comboItems.length})
                </div>
              </div>
            )}
            <div className="divide-y">
              {comboItems.map((comboItem, index) => (
                <div 
                  key={`combo-${index}`} 
                  className="p-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    {/* Combo Icon */}
                    <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center text-primary">
                      {getComboIcon(comboItem.combo.combo_type)}
                    </div>

                    {/* Combo Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm truncate">
                              {comboItem.combo.name}
                            </p>
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                              {comboItem.combo.combo_type === 'pizza' ? 'Pizza' : 
                               comboItem.combo.combo_type === 'rodizio' ? 'Rodízio' : 'Combo'}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {formatCurrency(comboItem.calculatedPrice)} x {comboItem.quantity}
                          </p>
                          {/* Show selections */}
                          {comboItem.selection.selectedFlavors && comboItem.selection.selectedFlavors.length > 0 && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {comboItem.selection.selectedFlavors.map(f => f.productName).join(', ')}
                            </p>
                          )}
                          {comboItem.selection.selectedChoices && comboItem.selection.selectedChoices.length > 0 && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {comboItem.selection.selectedChoices.map((c, i) => (
                                <span key={i}>
                                  {c.choiceLabel}: {c.productName}
                                  {i < comboItem.selection.selectedChoices!.length - 1 && ', '}
                                </span>
                              ))}
                            </div>
                          )}
                          {comboItem.notes && (
                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" />
                              {comboItem.notes}
                            </p>
                          )}
                        </div>
                        <span className="font-semibold text-sm shrink-0">
                          {formatCurrency(comboItem.calculatedPrice * comboItem.quantity)}
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 mt-2">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => onUpdateComboQuantity?.(index, comboItem.quantity - 1)}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <Input
                            type="number"
                            value={comboItem.quantity}
                            onChange={(e) => onUpdateComboQuantity?.(index, parseInt(e.target.value) || 0)}
                            className="h-7 w-12 text-center px-1 text-sm"
                            min={1}
                          />
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => onUpdateComboQuantity?.(index, comboItem.quantity + 1)}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => onRemoveCombo?.(index)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </ScrollArea>

      {/* Cancel Confirmation Dialog */}
      <CancelSentItemDialog
        open={cancelDialog.open}
        onOpenChange={(open) => setCancelDialog({ ...cancelDialog, open })}
        item={cancelDialog.item}
        onConfirm={handleConfirmCancel}
        isCancelling={false}
      />
    </>
  );
}
